import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { useEffect, useState } from 'react';
import { ButtonLink, Caps, Card, SectionLabel } from '../../design';
import { pagePricing, priced, pricingText } from '../../lib/pricing';
import { AnalyticsEvent, ctaEvent } from '../../utils/analytics';
import { AUDIENCES, audienceFrom, MKT } from './links';
import { MarketingLayout } from './MarketingLayout';
import { EVERY_PLAN, PLANS } from './PricingPage.data';
import './pricing.css';
// Two ways to pay, one page: dealership plans (monthly, by sheets read) and Import my own (a
// sign-up that covers the first car, then per car won). A tab per audience — #dealer or
// #import-my-own, or ?for= from a link — and a comparison under both. Every figure is live:
// pricingText()/priced() as everywhere (lib/pricing.ts, server/routes/pricing_page.go).
const TAB = {
    dealer: {
        hash: 'dealer',
        label: 'Dealership plans',
        k: '商'
    },
    individual: {
        hash: 'import-my-own',
        label: 'Import my own',
        k: '個'
    }
};
function audienceFromLocation() {
    const hash = window.location.hash.replace('#', '');
    if (hash === TAB.individual.hash) return 'individual';
    if (hash === TAB.dealer.hash) return 'dealer';
    return audienceFrom(window.location.search);
}
export default function PricingPage() {
    const t = pricingText(pagePricing());
    // The prerendered page opens on the dealership tab; a link's tab applies once it's running.
    const [tab, setTab] = useState('dealer');
    useEffect(()=>{
        const a = audienceFromLocation();
        if (a) setTab(a);
        const onHash = ()=>{
            const b = audienceFromLocation();
            if (b) setTab(b);
        };
        window.addEventListener('hashchange', onHash);
        return ()=>window.removeEventListener('hashchange', onHash);
    }, []);
    const choose = (a, focus = false)=>{
        setTab(a);
        window.history.replaceState(null, '', `#${TAB[a].hash}`);
        if (focus) document.getElementById(`mk-pricing-tab-${a}`)?.focus();
    };
    const onKey = (e)=>{
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            choose(tab === 'dealer' ? 'individual' : 'dealer', true);
        }
    };
    return /*#__PURE__*/ _jsx(MarketingLayout, {
        testid: Testids.app.marketing.pricingRoot,
        children: /*#__PURE__*/ _jsxs("main", {
            className: "mk-pricing",
            children: [
                /*#__PURE__*/ _jsx(SectionLabel, {
                    k: "料金",
                    className: "mk-pricing__label",
                    children: "Pricing"
                }),
                /*#__PURE__*/ _jsx("h1", {
                    className: "mk-pricing__title",
                    children: "Two ways to pay, depending on who’s importing."
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mk-pricing__intro",
                    children: "A dealership pays monthly, priced by the auction sheets it reads. Someone bringing in a car of their own pays once to sign up, with the first car included, and then per car they win. Either way you’re paying for the software. The car, your exporter, shipping, duty and the broker all get paid directly."
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mk-pricing__tabs",
                    role: "tablist",
                    "aria-label": "Who’s importing",
                    onKeyDown: onKey,
                    children: AUDIENCES.map((a)=>/*#__PURE__*/ _jsxs("button", {
                            id: `mk-pricing-tab-${a}`,
                            type: "button",
                            role: "tab",
                            "aria-selected": tab === a,
                            "aria-controls": `mk-pricing-panel-${a}`,
                            tabIndex: tab === a ? 0 : -1,
                            className: "mk-pricing__tab",
                            "data-testid": Testids.app.marketing.pricingTab(a),
                            onClick: ()=>choose(a),
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "mk-pricing__tab-k",
                                    "aria-hidden": "true",
                                    children: TAB[a].k
                                }),
                                TAB[a].label
                            ]
                        }, a))
                }),
                /*#__PURE__*/ _jsxs("div", {
                    id: "mk-pricing-panel-dealer",
                    role: "tabpanel",
                    "aria-labelledby": "mk-pricing-tab-dealer",
                    hidden: tab !== 'dealer',
                    className: "mk-pricing__panel",
                    "data-testid": Testids.app.marketing.pricingPanel('dealer'),
                    children: [
                        /*#__PURE__*/ _jsxs("p", {
                            className: "mk-pricing__panel-lead",
                            children: [
                                "For dealerships and shops buying to sell. Start on one of these plans, billed monthly.",
                                ' ',
                                /*#__PURE__*/ _jsx("a", {
                                    href: MKT.forDealers,
                                    children: "What a dealership gets →"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mk-pricing__plans",
                            children: PLANS.map((p)=>/*#__PURE__*/ _jsxs(Card, {
                                    shadow: p.featured ? 'lg' : 'sm',
                                    className: `mk-pricing__plan${p.featured ? ' mk-pricing__plan--featured' : ''}`,
                                    children: [
                                        p.featured && /*#__PURE__*/ _jsx("span", {
                                            className: "mk-pricing__badge",
                                            children: "Most shops"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-pricing__plan-head",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-pricing__plan-k",
                                                    "aria-hidden": "true",
                                                    children: p.k
                                                }),
                                                /*#__PURE__*/ _jsx("h2", {
                                                    className: "mk-pricing__plan-name",
                                                    children: p.name
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "mk-pricing__who",
                                            children: p.who
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-pricing__price",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-pricing__amount",
                                                    ...priced(`plan.${p.id}.price`),
                                                    children: t[`plan.${p.id}.price`]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-pricing__per",
                                                    children: "/ month"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-pricing__annual",
                                            children: [
                                                "or ",
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced(`plan.${p.id}.annual`),
                                                    children: t[`plan.${p.id}.annual`]
                                                }),
                                                " / year, two months free"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-pricing__sheets",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-pricing__allow",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__sheets-n",
                                                            ...priced(`plan.${p.id}.sheets`),
                                                            children: t[`plan.${p.id}.sheets`]
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__sheets-l",
                                                            children: "auction sheets read a month"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mk-pricing__allow",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__sheets-n",
                                                            ...priced(`plan.${p.id}.comps`),
                                                            children: t[`plan.${p.id}.comps`]
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__sheets-l",
                                                            children: "bid estimates a month"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("ul", {
                                            className: "mk-pricing__lines",
                                            children: [
                                                /*#__PURE__*/ _jsxs("li", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__check",
                                                            children: "✓"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced(`plan.${p.id}.users`),
                                                            children: t[`plan.${p.id}.users`]
                                                        })
                                                    ]
                                                }),
                                                p.lines.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "mk-pricing__check",
                                                                children: "✓"
                                                            }),
                                                            l
                                                        ]
                                                    }, l))
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(ButtonLink, {
                                            href: MKT.signupFor('dealer'),
                                            ...ctaEvent(AnalyticsEvent.ctaSignup, 'pricing-plan', 'dealer'),
                                            "data-umami-event-plan": p.name,
                                            variant: p.featured ? undefined : 'secondary',
                                            className: "mk-pricing__plan-cta",
                                            children: "Get started"
                                        })
                                    ]
                                }, p.name))
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mk-pricing__below",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pricing__every",
                                    children: [
                                        /*#__PURE__*/ _jsx(Caps, {
                                            className: "mk-pricing__includes-head",
                                            children: "Every plan includes"
                                        }),
                                        /*#__PURE__*/ _jsx("ul", {
                                            className: "mk-pricing__list",
                                            children: EVERY_PLAN.map((line)=>/*#__PURE__*/ _jsxs("li", {
                                                    className: "mk-pricing__item",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__check",
                                                            children: "✓"
                                                        }),
                                                        line
                                                    ]
                                                }, line))
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pricing__explain",
                                    children: [
                                        /*#__PURE__*/ _jsx(Caps, {
                                            className: "mk-pricing__includes-head",
                                            children: "What counts"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "mk-pricing__text",
                                            children: "Every auction sheet we translate for you uses one sheet. A bid estimate prices a lot against sold auction results and live US dealer listings. That’s more work, so plans include fewer of them. Hunts estimate their strong and partial matches, and you can estimate any other lot yourself. We count a lot once, so the same car turning up in several hunts or on the next run is free."
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-pricing__text",
                                            children: [
                                                "Run out and nothing stops. Hunts keep searching and listing lots, and new ones wait for their sheet or estimate until the 1st. Or add",
                                                ' ',
                                                /*#__PURE__*/ _jsxs("b", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced('pack.sheets.count'),
                                                            children: t['pack.sheets.count']
                                                        }),
                                                        " more sheets for ",
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced('pack.sheets.price'),
                                                            children: t['pack.sheets.price']
                                                        })
                                                    ]
                                                }),
                                                ' ',
                                                "or",
                                                ' ',
                                                /*#__PURE__*/ _jsxs("b", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced('pack.comps.count'),
                                                            children: t['pack.comps.count']
                                                        }),
                                                        " more bid estimates for ",
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced('pack.comps.price'),
                                                            children: t['pack.comps.price']
                                                        })
                                                    ]
                                                }),
                                                "."
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "mk-pricing__addon",
                            "data-testid": Testids.app.marketing.pricingSiteAddon,
                            children: [
                                /*#__PURE__*/ _jsx("b", {
                                    children: "Add-on · Dealer website."
                                }),
                                " One of five themes in your name, hosted by us and synced both ways with your stock, for",
                                ' ',
                                /*#__PURE__*/ _jsxs("b", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            ...priced('site.monthly'),
                                            children: t['site.monthly']
                                        }),
                                        " a month"
                                    ]
                                }),
                                ' ',
                                "plus ",
                                /*#__PURE__*/ _jsx("span", {
                                    ...priced('site.setup'),
                                    children: t['site.setup']
                                }),
                                " setup (setup on Dealer:",
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    ...priced('site.dealer'),
                                    children: t['site.dealer']
                                }),
                                ").",
                                ' ',
                                /*#__PURE__*/ _jsx("a", {
                                    href: MKT.dealerWebsites,
                                    children: "See the themes →"
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    id: "mk-pricing-panel-individual",
                    role: "tabpanel",
                    "aria-labelledby": "mk-pricing-tab-individual",
                    hidden: tab !== 'individual',
                    className: "mk-pricing__panel",
                    "data-testid": Testids.app.marketing.pricingPanel('individual'),
                    children: [
                        /*#__PURE__*/ _jsxs("p", {
                            className: "mk-pricing__panel-lead",
                            children: [
                                "For one person bringing in a JDM car or truck for themselves, in their own name or their LLC’s. ",
                                /*#__PURE__*/ _jsx("a", {
                                    href: MKT.importMyOwn,
                                    children: "How Import my own works →"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Card, {
                            shadow: "md",
                            className: "mk-pricing__own",
                            "data-testid": Testids.app.marketing.pricingOwn,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pricing__own-stub",
                                    "aria-hidden": "true",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-pricing__own-k",
                                            children: "個"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "mk-pricing__own-days",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.days'),
                                                    children: t['pass.days']
                                                }),
                                                " days"
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pricing__own-body",
                                    children: [
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "mk-pricing__plan-name",
                                            children: "Import my own"
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-pricing__who",
                                            children: [
                                                "Searching is included for ",
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.days'),
                                                    children: t['pass.days']
                                                }),
                                                ' ',
                                                "days from sign-up, and again from each car pass. You pay per car, with no monthly fee."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("ul", {
                                            className: "mk-pricing__lines mk-pricing__own-lines",
                                            children: [
                                                /*#__PURE__*/ _jsxs("li", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__check",
                                                            children: "✓"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    ...priced('pass.hunts'),
                                                                    children: t['pass.hunts']
                                                                }),
                                                                " watching the auctions for your car, included"
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("li", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__check",
                                                            children: "✓"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    ...priced('pass.sheets'),
                                                                    children: t['pass.sheets']
                                                                }),
                                                                " auction sheets read and ",
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    ...priced('pass.comps'),
                                                                    children: t['pass.comps']
                                                                }),
                                                                " bid estimates every",
                                                                ' ',
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    ...priced('pass.days'),
                                                                    children: t['pass.days']
                                                                }),
                                                                " days, included"
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("li", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__check",
                                                            children: "✓"
                                                        }),
                                                        "For each car you win: the customs broker hand-off, US transport, your documents and the car’s landed cost"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("li", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "mk-pricing__check",
                                                            children: "✓"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            children: [
                                                                "Signing up covers your first car. After that,",
                                                                ' ',
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    ...priced('pass.price'),
                                                                    children: t['pass.price']
                                                                }),
                                                                " per car you win."
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pricing__own-price",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-pricing__amount",
                                            ...priced('pass.price'),
                                            children: t['pass.price']
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-pricing__per",
                                            children: "to sign up, your first car included"
                                        }),
                                        /*#__PURE__*/ _jsx(ButtonLink, {
                                            href: MKT.signupFor('individual'),
                                            ...ctaEvent(AnalyticsEvent.ctaSignup, 'pricing-plan', 'individual'),
                                            "data-umami-event-plan": "Import my own",
                                            className: "mk-pricing__plan-cta",
                                            children: "Start importing my own"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mk-pricing__below",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pricing__explain mk-pricing__guarantee",
                                    "data-testid": Testids.app.marketing.pricingGuarantee,
                                    children: [
                                        /*#__PURE__*/ _jsx(Caps, {
                                            className: "mk-pricing__includes-head",
                                            children: "The sign-up guarantee"
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-pricing__text",
                                            children: [
                                                "If you haven’t won a car, ask for your sign-up back any time. We suggest giving it",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('guarantee.days'),
                                                    children: t['guarantee.days']
                                                }),
                                                " days, and you can ask until ",
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('guarantee.claim'),
                                                    children: t['guarantee.claim']
                                                }),
                                                " days after that. We refund your sign-up minus what you used, at retail prices that are currently ",
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('guarantee.sheet'),
                                                    children: t['guarantee.sheet']
                                                }),
                                                " per auction sheet and ",
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('guarantee.comp'),
                                                    children: t['guarantee.comp']
                                                }),
                                                ' ',
                                                "per bid estimate."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-pricing__text",
                                            children: [
                                                "Those prices follow the top-up packs, so they can change. The app shows your exact amount before you ask, and asking freezes it. Top-ups you bought aren’t charged again, and they aren’t refunded either. The refund goes back to your original payment method, and your hunts pause until you add a car pass. The guarantee is for anyone who hasn’t won or bought a car since signing up, through us or anywhere else. That’s why we keep a record of every bid you send through the app, and why, when you ask, we search your connected mailbox for win and purchase emails (see the",
                                                ' ',
                                                /*#__PURE__*/ _jsx("a", {
                                                    href: `${MKT.terms}#guarantee`,
                                                    children: "Terms of Service"
                                                }),
                                                ")."
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pricing__explain",
                                    children: [
                                        /*#__PURE__*/ _jsx(Caps, {
                                            className: "mk-pricing__includes-head",
                                            children: "What it doesn’t include"
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-pricing__text",
                                            children: [
                                                "You pay for the car, and everything it takes to get it here, to the people who do the work. That’s the auction price and fees to your exporter, ocean freight, duty to US Customs, the broker’s fee and the truck from the port.",
                                                ' ',
                                                /*#__PURE__*/ _jsx("a", {
                                                    href: `${MKT.importMyOwn}#costs`,
                                                    children: "A rough illustration of those costs →"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "mk-pricing__compare",
                    "data-testid": Testids.app.marketing.pricingCompare,
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "mk-pricing__compare-title",
                            children: "Which fits you?"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mk-pricing__table-wrap",
                            children: /*#__PURE__*/ _jsxs("table", {
                                className: "mk-pricing__table",
                                children: [
                                    /*#__PURE__*/ _jsx("thead", {
                                        children: /*#__PURE__*/ _jsxs("tr", {
                                            children: [
                                                /*#__PURE__*/ _jsx("th", {
                                                    scope: "col",
                                                    children: /*#__PURE__*/ _jsx("span", {
                                                        className: "rhd-sr-only",
                                                        children: "Feature"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("th", {
                                                    scope: "col",
                                                    children: "Dealership plans"
                                                }),
                                                /*#__PURE__*/ _jsx("th", {
                                                    scope: "col",
                                                    children: "Import my own"
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("tbody", {
                                        children: [
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "Who it’s for"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "Shops and dealers buying to sell, for stock and for customers"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "One person importing a car for themselves, in their own name or an LLC’s"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "How you pay"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("td", {
                                                        children: [
                                                            "Monthly, from ",
                                                            /*#__PURE__*/ _jsx("span", {
                                                                ...priced('from'),
                                                                children: t.from
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("td", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                ...priced('pass.price'),
                                                                children: t['pass.price']
                                                            }),
                                                            " to sign up (first car included), then per car you win"
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "Hunts"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "As many as you like"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("td", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                ...priced('pass.hunts'),
                                                                children: t['pass.hunts']
                                                            }),
                                                            " at a time, free"
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "People"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "Seats by plan, with owner, admin and member roles"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "Just you"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "Customers"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "Hunts per customer, match alerts and weekly digests from your mailbox"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "—"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "Customs, shipping, documents"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "Every car"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "Every car you win"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "Sale and profit per car"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "Every car"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "If you decide to resell it"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        scope: "row",
                                                        children: "Guarantee"
                                                    }),
                                                    /*#__PURE__*/ _jsx("td", {
                                                        children: "—"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("td", {
                                                        children: [
                                                            "Haven’t won a car? Sign-up refunded, minus what you used (we suggest",
                                                            ' ',
                                                            /*#__PURE__*/ _jsx("span", {
                                                                ...priced('guarantee.days'),
                                                                children: t['guarantee.days']
                                                            }),
                                                            " days first)"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mk-pricing__compare-ctas",
                            children: [
                                /*#__PURE__*/ _jsx(ButtonLink, {
                                    href: MKT.signupFor('dealer'),
                                    variant: "secondary",
                                    ...ctaEvent(AnalyticsEvent.ctaSignup, 'pricing-compare', 'dealer'),
                                    children: "Get started as a dealership"
                                }),
                                /*#__PURE__*/ _jsx(ButtonLink, {
                                    href: MKT.signupFor('individual'),
                                    variant: "secondary",
                                    ...ctaEvent(AnalyticsEvent.ctaSignup, 'pricing-compare', 'individual'),
                                    children: "Start importing my own"
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
